/** Money. Integer cents, always. The only place that formats a euro amount. */

/** An amount in whole euro cents. Never a euro float. */
export type Cents = number & { readonly __brand: "Cents" };

/** Construct a Cents from an integer. Throws on anything that isn't one. */
export function cents(value: number): Cents {
  if (!Number.isSafeInteger(value)) {
    throw new TypeError(`Money must be a safe integer number of cents, got ${value}`);
  }
  return value as Cents;
}

/**
 * A euro amount written in source: `euros(7.5)` → 750.
 *
 * For tariff literals and tests. Not for user input, and not for a value that
 * has already been through arithmetic.
 */
export function euros(value: number): Cents {
  const scaled = value * 100;
  const rounded = Math.round(scaled);
  // 7.5 * 100 is 750.0000000000001 in binary floating point, so compare with a
  // tolerance rather than for equality.
  if (!Number.isFinite(scaled) || Math.abs(scaled - rounded) > 1e-6) {
    throw new TypeError(`${value} is not a whole number of cents`);
  }
  return cents(rounded);
}

/**
 * A euro amount TYPED BY A HUMAN → cents, or null when it isn't one. The only
 * sanctioned path from an input field to money (0.25 tariff forms): no float
 * arithmetic — the halves around the decimal separator parse as integers.
 *
 * nl-first separator rules:
 *  - both . and , present → the LAST one is the decimal separator, the other
 *    must group in threes ("1.234,56" and "1,234.56" → 123456)
 *  - only , → decimal separator, 1–2 decimals ("4,5" → 450; "4,999" → null)
 *  - only . → decimal when a single dot has 1–2 digits behind it ("4.50");
 *    otherwise nl grouping in threes ("1.000" → 100000; "1.2.3" → null)
 *
 * Worked examples: "4" → 400 · "€ 200" → 20000 · "4,50" → 450 · "-4" → null.
 */
export function parseEuros(invoer: string): Cents | null {
  // Strip the currency dressing people paste in: €, spaces, nbsp.
  const kaal = invoer.replace(/[€\s ]/g, "");
  if (kaal === "" || !/^[\d.,]+$/.test(kaal)) return null;

  const laatstePunt = kaal.lastIndexOf(".");
  const laatsteKomma = kaal.lastIndexOf(",");
  let heel = kaal;
  let decimalen = "";

  if (laatstePunt !== -1 && laatsteKomma !== -1) {
    const scheider = Math.max(laatstePunt, laatsteKomma);
    heel = kaal.slice(0, scheider);
    decimalen = kaal.slice(scheider + 1);
    const groep = scheider === laatstePunt ? "," : ".";
    if (!new RegExp(`^\\d{1,3}(\\${groep}\\d{3})*$`).test(heel)) return null;
    if (!/^\d{1,2}$/.test(decimalen)) return null;
    heel = heel.split(groep).join("");
  } else if (laatsteKomma !== -1) {
    if (kaal.indexOf(",") !== laatsteKomma) return null;
    heel = kaal.slice(0, laatsteKomma);
    decimalen = kaal.slice(laatsteKomma + 1);
    if (!/^\d+$/.test(heel) || !/^\d{1,2}$/.test(decimalen)) return null;
  } else if (laatstePunt !== -1) {
    const staart = kaal.slice(laatstePunt + 1);
    if (kaal.indexOf(".") === laatstePunt && /^\d{1,2}$/.test(staart)) {
      heel = kaal.slice(0, laatstePunt);
      decimalen = staart;
      if (!/^\d+$/.test(heel)) return null;
    } else {
      if (!/^\d{1,3}(\.\d{3})+$/.test(kaal)) return null;
      heel = kaal.split(".").join("");
    }
  }

  const helen = Number.parseInt(heel, 10);
  const centDeel = decimalen === "" ? 0 : Number.parseInt(decimalen.padEnd(2, "0"), 10);
  // Far above any real tariff: an extra-zero typo is refused, not published.
  if (!Number.isSafeInteger(helen) || helen > 10_000_000) return null;
  return cents(helen * 100 + centDeel);
}

export function addCents(...amounts: Cents[]): Cents {
  return cents(amounts.reduce<number>((total, amount) => total + amount, 0));
}

export function subtractCents(a: Cents, b: Cents): Cents {
  return cents(a - b);
}

/**
 * Multiply by a count or a rate, rounding half away from zero.
 *
 * Worked example: €7,50 per kind × 43 kinderen = 750 × 43 = €322,50.
 *
 * Not `Math.round`, which rounds -0.5 to -0 — a credit note would then round
 * differently from the invoice it reverses.
 */
export function multiplyCents(amount: Cents, factor: number): Cents {
  if (!Number.isFinite(factor)) {
    throw new TypeError(`Cannot multiply money by ${factor}`);
  }
  const product = amount * factor;
  const rounded = Math.sign(product) * Math.round(Math.abs(product));
  return cents(rounded === 0 ? 0 : rounded);
}

/** Worked example: 30% annuleringskost op €600,00 → 60_000 × 0,30 = €180,00. */
export function percentOfCents(amount: Cents, percent: number): Cents {
  return multiplyCents(amount, percent / 100);
}

const EURO_FORMAT = new Intl.NumberFormat("nl-BE", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Any Unicode space separator that is not a plain space.
 *
 * Intl puts U+00A0 or U+202F around the currency symbol depending on locale
 * data, and which one it picks has changed between ICU versions. Matching the
 * whole category rather than two literal characters means an ICU upgrade cannot
 * quietly break invoice formatting — and the lookahead keeps an already-plain
 * space from matching, so the result is idempotent.
 */
export const INTL_SPACES = /(?! )\p{Zs}/gu;

/** Format for display: 123_456 → "€ 1.234,56". Never store or transmit the result. */
export function formatEuros(amount: Cents): string {
  // Normalise to a plain space so the output survives copy-paste into email.
  // Never write the space characters as literals: they are invisible in a diff,
  // and Biome silently rewrote the first version of this line into a regex that
  // meant something else entirely.
  return EURO_FORMAT.format(amount / 100).replace(INTL_SPACES, " ");
}
